import { type RefObject, useLayoutEffect, useRef } from "react";
import type { MapRef } from "react-map-gl";

const eventName = "edges:prepare-map-export";
type Release = () => void;
type Prepare = (signal: AbortSignal) => Promise<Release>;
const activeMaps = new WeakSet<HTMLElement>();
type ExportRequest = { signal: AbortSignal; tasks: Promise<Release>[] };

export function useMapExportRegistration(ref: RefObject<HTMLDivElement | null>, prepare: Prepare) {
  const prepareRef = useRef(prepare);
  useLayoutEffect(() => {
    prepareRef.current = prepare;
  });
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    element.dataset.edgesMap = "";
    const listener = (event: Event) => {
      const { signal, tasks } = (event as CustomEvent<ExportRequest>).detail;
      if (activeMaps.has(element)) {
        tasks.push(Promise.reject(new Error("This map is already being exported.")));
        return;
      }
      activeMaps.add(element);
      tasks.push(
        Promise.resolve()
          .then(() => prepareRef.current(signal))
          .then(
            (restore) => {
              let released = false;
              return () => {
                if (!released) {
                  released = true;
                  try {
                    restore();
                  } finally {
                    activeMaps.delete(element);
                  }
                }
              };
            },
            (error) => {
              activeMaps.delete(element);
              throw error;
            }
          )
      );
    };
    element.addEventListener(eventName, listener);
    return () => {
      element.removeEventListener(eventName, listener);
      delete element.dataset.edgesMap;
    };
  }, [ref]);
}

export async function waitForMapReady(check: () => boolean, signal: AbortSignal): Promise<void> {
  signal.throwIfAborted();
  if (check()) return;
  await new Promise<void>((resolve, reject) => {
    const finish = (error?: unknown) => {
      clearInterval(timer);
      signal.removeEventListener("abort", abort);
      if (error) reject(error);
      else resolve();
    };
    const abort = () => finish(signal.reason ?? new Error("Map export cancelled."));
    const timer = setInterval(() => {
      try {
        if (check()) finish();
      } catch (error) {
        finish(error);
      }
    }, 25);
    signal.addEventListener("abort", abort, { once: true });
  });
}

/** Prepare only maps inside this capture. Always release in the host's finally block. */
export async function prepareMapsForExport(root: HTMLElement, signal?: AbortSignal): Promise<Release> {
  const controller = new AbortController();
  const abort = () => controller.abort(signal?.reason);
  signal?.addEventListener("abort", abort, { once: true });
  if (signal?.aborted) abort();
  const timer = setTimeout(() => controller.abort(new Error("The map took too long to load. Try again.")), 15000);
  const tasks: Promise<Release>[] = [];
  const elements = [
    ...(root.matches("[data-edges-map]") ? [root] : []),
    ...root.querySelectorAll<HTMLElement>("[data-edges-map]"),
  ];
  try {
    controller.signal.throwIfAborted();
    for (const element of elements)
      element.dispatchEvent(
        new CustomEvent<ExportRequest>(eventName, { detail: { signal: controller.signal, tasks } })
      );
    const releases = await Promise.all(tasks);
    return () => {
      for (const release of releases.reverse()) release();
    };
  } catch (error) {
    controller.abort(error);
    const results = await Promise.allSettled(tasks);
    for (const result of results) if (result.status === "fulfilled") result.value();
    throw error;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}

/** Capture during a GL render event so the drawing buffer has not been cleared. */
export async function prepareGLMap(
  getMap: () => MapRef | null,
  signal: AbortSignal,
  hasError: () => boolean = () => false
): Promise<Release> {
  await waitForMapReady(() => {
    if (hasError()) throw new Error("Mapbox could not load all map sources. Check the token and source access.");
    return Boolean(getMap()?.getMap().loaded());
  }, signal);
  signal.throwIfAborted();
  const map = getMap()?.getMap();
  if (!map) throw new Error("Map unavailable for export.");
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const cleanup = () => {
      map.off("render", capture);
      signal.removeEventListener("abort", abort);
    };
    const abort = () => {
      cleanup();
      reject(signal.reason);
    };
    const capture = () => {
      cleanup();
      try {
        if (hasError()) throw new Error("Map sources failed.");
        resolve(map.getCanvas().toDataURL("image/png"));
      } catch {
        reject(new Error("The map could not be captured. Check its sources and try again."));
      }
    };
    signal.addEventListener("abort", abort, { once: true });
    try {
      map.once("render", capture);
      map.triggerRepaint();
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
  signal.throwIfAborted();
  const canvas = map.getCanvas();
  const image = new Image();
  image.src = dataUrl;
  await new Promise<void>((resolve, reject) => {
    const abort = () => {
      signal.removeEventListener("abort", abort);
      reject(signal.reason);
    };
    signal.addEventListener("abort", abort, { once: true });
    image.decode().then(
      () => {
        signal.removeEventListener("abort", abort);
        resolve();
      },
      () => {
        signal.removeEventListener("abort", abort);
        reject(new Error("The captured map image could not be decoded."));
      }
    );
  });
  signal.throwIfAborted();
  image.style.cssText = canvas.style.cssText;
  image.style.position = "absolute";
  image.style.inset = "0";
  image.style.pointerEvents = "none";
  image.alt = "Map";
  const visibility = canvas.style.visibility;
  canvas.after(image);
  canvas.style.visibility = "hidden";
  return () => {
    image.remove();
    canvas.style.visibility = visibility;
  };
}
